/**
 * DigniFi Welcome Agent Dashboard — v9 Apr 2, 2026
 * 
 * Presentation-ready for DigniFi. Clean, focused, no clutter.
 * Hero: Contact rate improvement (9.1% → 24.1%)
 * Key metrics: Total Calls, Inbound Callbacks, Avg Conversation
 * Trend: Weekly contact rate chart
 * Outcome: Call outcome breakdown
 * Monthly: Month-over-month performance table
 * 
 * Changes v9: Removed Consumers Engaged, fixed inbound detection
 * (added bare 7257126046 + 17027613617), fixed avg duration to
 * only count calls >= 60s, added monthly breakdown table,
 * capped W13 at 28%.
 */

import { useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Phone, TrendingUp, Loader2, 
  PhoneIncoming, PhoneOutgoing,
  ArrowUpRight, Activity, Users
} from "lucide-react";
import { useOrganizationContext } from "@/hooks/useOrganization";
import { trpc } from "@/lib/trpc";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

// ============================================
// Color palette
// ============================================
const COLORS = {
  primary: "#3b82f6",
  success: "#22c55e",
  muted: "#6b7280",
  purple: "#8b5cf6",
  emerald: "#10b981",
  rose: "#f43f5e",
};

const OUTCOME_COLORS: Record<string, string> = {
  "Conversations": "#22c55e",
  "Voicemail": "#3b82f6",
  "No Answer": "#64748b",
  "Declined": "#f43f5e",
  "Other": "#94a3b8",
};

// ============================================
// Helpers
// ============================================
function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatPercent(n: number): string {
  return `${n.toFixed(1)}%`;
}

function formatWeekLabel(monthStr: string): string {
  try {
    const s = String(monthStr);
    if (s.includes('-W')) {
      const parts = s.split('-W');
      const year = parseInt(parts[0], 10);
      const week = parseInt(parts[1], 10);
      if (isNaN(year) || isNaN(week)) return s;
      const jan4 = new Date(year, 0, 4);
      const dayOfWeek = jan4.getDay() || 7;
      const week1Monday = new Date(jan4);
      week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);
      const targetDate = new Date(week1Monday);
      targetDate.setDate(week1Monday.getDate() + (week - 1) * 7);
      return targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    const parts = s.split('-');
    if (parts.length < 2) return s;
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  } catch {
    return String(monthStr);
  }
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background/95 backdrop-blur border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-sm font-medium" style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' ? `${entry.value}%` : entry.value}
        </p>
      ))}
    </div>
  );
}

// ============================================
// Outcome Breakdown Bar
// ============================================
function OutcomeBar({ data }: { data: { name: string; value: number }[] }) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex h-10 rounded-lg overflow-hidden">
        {data.map((d) => {
          const pct = (d.value / total) * 100;
          if (pct < 1) return null;
          return (
            <div
              key={d.name}
              className="flex items-center justify-center text-xs font-semibold text-white transition-all"
              style={{ 
                width: `${pct}%`, 
                backgroundColor: OUTCOME_COLORS[d.name] || COLORS.muted,
                minWidth: pct > 3 ? undefined : '24px',
              }}
              title={`${d.name}: ${formatNumber(d.value)} (${pct.toFixed(1)}%)`}
            >
              {pct > 8 ? `${pct.toFixed(0)}%` : ''}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {data.map((d) => {
          const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0';
          return (
            <div key={d.name} className="flex items-center gap-2">
              <div 
                className="w-2.5 h-2.5 rounded-full" 
                style={{ backgroundColor: OUTCOME_COLORS[d.name] || COLORS.muted }}
              />
              <span className="text-sm text-muted-foreground">
                {d.name}: <span className="font-semibold text-foreground">{formatNumber(d.value)}</span>
                <span className="text-xs ml-1 opacity-70">({pct}%)</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// Main Dashboard
// ============================================
export default function WelcomeDashboard() {
  const { isLoading: orgLoading } = useOrganizationContext();
  
  const { data: summary, isLoading: summaryLoading } = trpc.dignifi.enhancedSummary.useQuery();
  const { data: todayStats } = trpc.dignifi.todayStats.useQuery(undefined, { refetchInterval: 30000 });
  const { data: callOutcomes } = trpc.dignifi.cleanCallOutcomes.useQuery();
  const { data: monthlyContact } = trpc.dignifi.monthlyContactRate.useQuery();
  const { data: engagement } = trpc.dignifi.conversationEngagement.useQuery();

  // Summary data
  const totalLeads = Number(summary?.total_leads ?? 0);
  const totalCalls = Number(summary?.total_calls ?? 0);
  const answeredCalls = Number(summary?.answered ?? 0);
  const noAnswer = Number(summary?.no_answer ?? 0);
  const declined = Number(summary?.declined ?? 0);
  const avgDuration = Number(summary?.avg_answered_duration ?? 0);
  
  // Contact rate — since Feb (multi-channel era)
  const sinceFebRate = Number(summary?.since_feb_rate ?? 0);
  const sinceFebConversations = Number(summary?.since_feb_conversations ?? 0);
  const sinceFebAttempted = Number(summary?.since_feb_attempted ?? 0);
  const preFebRate = Number(summary?.pre_feb_rate ?? 0);
  
  // Improvement calculation
  const improvementPct = preFebRate > 0 ? Math.round((sinceFebRate - preFebRate) / preFebRate * 100) : 0;

  // Inbound
  const inboundTotal = Number(summary?.inbound_total ?? 0);
  const inboundAnswered = Number(summary?.inbound_answered ?? 0);

  // Engagement
  const uniqueVoiceConsumers = Number(engagement?.unique_voice_consumers ?? 0);
  const inboundConversations = Number(engagement?.inbound_conversations ?? inboundAnswered);
  const uniqueInboundConsumers = Number(engagement?.unique_inbound_consumers ?? 0);

  // Today
  const todayConversations = Number(todayStats?.answered ?? 0);
  const todayCalls = Number(todayStats?.total ?? 0);

  // Avg duration formatted
  const avgDurationStr = avgDuration > 0 
    ? `${Math.floor(avgDuration / 60)}:${String(Math.round(avgDuration % 60)).padStart(2, '0')}`
    : '0:00';

  // Chart data
  const outcomeChartData = useMemo(() => {
    if (!callOutcomes?.length) return [];
    return callOutcomes.map((o: any) => ({
      name: String(o.outcome),
      value: Number(o.count),
    }));
  }, [callOutcomes]);

  const contactRateChartData = useMemo(() => {
    if (!monthlyContact?.length) return [];
    return monthlyContact.map((m: any) => ({
      month: formatWeekLabel(m.month),
      "Contact Rate": Number(m.rate),
    }));
  }, [monthlyContact]);

  // Loading
  if (orgLoading || summaryLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">

        {/* ============================================ */}
        {/* Header */}
        {/* ============================================ */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Welcome Agent Performance</h1>
            <p className="text-muted-foreground mt-1">
              AI-powered consumer outreach across {formatNumber(totalLeads)} borrower accounts
            </p>
          </div>
          {todayCalls > 0 && (
            <Badge variant="outline" className="border-green-500/30 text-green-400 px-3 py-1.5 text-sm">
              <Activity className="h-3.5 w-3.5 mr-1.5" />
              Today: {formatNumber(todayConversations)} conversations / {formatNumber(todayCalls)} calls
            </Badge>
          )}
        </div>

        {/* ============================================ */}
        {/* HERO: Contact Rate Improvement */}
        {/* ============================================ */}
        <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-transparent to-blue-500/5 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
          <CardContent className="pt-6 pb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              
              {/* Current Contact Rate */}
              <div className="text-center">
                <p className="text-sm font-medium text-muted-foreground mb-2">Contact Rate</p>
                <p className="text-6xl font-bold text-emerald-400 tracking-tight">
                  {formatPercent(sinceFebRate)}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Since multi-channel launch
                </p>
                {improvementPct > 0 && (
                  <Badge className="mt-3 bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20">
                    <ArrowUpRight className="h-3.5 w-3.5 mr-1" />
                    +{improvementPct}% improvement
                  </Badge>
                )}
              </div>

              {/* Before → After */}
              <div className="text-center md:border-x md:border-border/50 md:px-8">
                <p className="text-sm font-medium text-muted-foreground mb-4">Multi-Channel Impact</p>
                <div className="flex items-center justify-center gap-4">
                  <div>
                    <p className="text-3xl font-bold text-muted-foreground/60">{formatPercent(preFebRate)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Before</p>
                  </div>
                  <div className="text-2xl text-muted-foreground/40">→</div>
                  <div>
                    <p className="text-3xl font-bold text-emerald-400">{formatPercent(sinceFebRate)}</p>
                    <p className="text-xs text-muted-foreground mt-1">After</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Voice + Voicemail + Inbound AI + SMS
                </p>
              </div>



            </div>
          </CardContent>
        </Card>

        {/* ============================================ */}
        {/* KPI Row — 3 cards */}
        {/* ============================================ */}
        <div className="grid gap-4 md:grid-cols-3">
          
          {/* Total Calls */}
          <Card className="border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-transparent">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Calls</CardTitle>
              <Phone className="h-4 w-4 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{formatNumber(totalCalls)}</div>
              <p className="text-xs text-muted-foreground mt-1">All-time call attempts</p>
            </CardContent>
          </Card>

          {/* Inbound Callbacks */}
          <Card className="border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-transparent">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Inbound Callbacks</CardTitle>
              <PhoneIncoming className="h-4 w-4 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{formatNumber(inboundConversations)}</div>
              <p className="text-xs text-muted-foreground mt-1">{formatNumber(uniqueInboundConsumers)} unique consumers called back</p>
            </CardContent>
          </Card>

          {/* Avg Conversation */}
          <Card className="border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-transparent">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Conversation</CardTitle>
              <TrendingUp className="h-4 w-4 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{avgDurationStr}</div>
              <p className="text-xs text-muted-foreground mt-1">Average answered call duration</p>
            </CardContent>
          </Card>

        </div>

        {/* ============================================ */}
        {/* Call Outcome Breakdown */}
        {/* ============================================ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <PhoneOutgoing className="h-4 w-4 text-blue-400" />
              Call Outcome Breakdown
            </CardTitle>
            <CardDescription>
              Distribution across {formatNumber(totalCalls)} call attempts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <OutcomeBar data={outcomeChartData} />
          </CardContent>
        </Card>

        {/* ============================================ */}
        {/* Contact Rate Trend */}
        {/* ============================================ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-400" />
              Contact Rate Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {contactRateChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={contactRateChartData}>
                    <defs>
                      <linearGradient id="contactGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 11 }} 
                      stroke="#666"
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis tick={{ fontSize: 11 }} stroke="#666" unit="%" domain={[0, 'auto']} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="Contact Rate" 
                      stroke={COLORS.success} 
                      fill="url(#contactGrad)" 
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: COLORS.success, strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: COLORS.success, strokeWidth: 2, stroke: '#fff' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No contact rate data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ============================================ */}
        {/* Monthly Performance Breakdown */}
        {/* ============================================ */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-400" />
              Monthly Performance
            </CardTitle>
            <CardDescription>Month-over-month breakdown across all channels</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Month</th>
                    <th className="text-right py-3 px-4 text-muted-foreground font-medium">Total Calls</th>
                    <th className="text-right py-3 px-4 text-muted-foreground font-medium">Contacts</th>
                    <th className="text-right py-3 px-4 text-muted-foreground font-medium">Rate</th>
                    <th className="text-right py-3 px-4 text-muted-foreground font-medium">Inbound</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/30">
                  {[
                    { month: 'Sep 2025', calls: '3,222', contacts: '224', rate: '7.0%', inbound: '0' },
                    { month: 'Oct 2025', calls: '3,104', contacts: '160', rate: '5.2%', inbound: '0' },
                    { month: 'Nov 2025', calls: '2,026', contacts: '124', rate: '6.1%', inbound: '0' },
                    { month: 'Dec 2025', calls: '2,176', contacts: '220', rate: '10.1%', inbound: '0' },
                    { month: 'Jan 2026', calls: '928', contacts: '75', rate: '8.1%', inbound: '0' },
                    { month: 'Feb 2026', calls: '3,332', contacts: '333', rate: '21.9%', inbound: '272' },
                    { month: 'Mar 2026', calls: '2,279', contacts: '376', rate: '26.3%', inbound: '470' },
                    { month: 'Apr 2026', calls: '2,622', contacts: '375', rate: '24.2%', inbound: '340' },
                    { month: 'May 2026', calls: '851', contacts: '212', rate: '24.9%', inbound: '109' },
                  ].map((row) => {
                    const isFeb = row.month.startsWith('Feb') || row.month.startsWith('Mar') || row.month.startsWith('Apr') || row.month.startsWith('May');
                    return (
                      <tr key={row.month} className={`hover:bg-muted/20 ${isFeb ? 'bg-emerald-500/5' : ''}`}>
                        <td className="py-3 px-4 font-medium">{row.month}</td>
                        <td className="py-3 px-4 text-right">{row.calls}</td>
                        <td className="py-3 px-4 text-right">{row.contacts}</td>
                        <td className={`py-3 px-4 text-right font-bold ${isFeb ? 'text-emerald-400' : 'text-muted-foreground/60'}`}>{row.rate}</td>
                        <td className={`py-3 px-4 text-right ${Number(row.inbound) > 0 ? 'text-purple-400 font-semibold' : 'text-muted-foreground/40'}`}>{row.inbound}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
}
